import { Injectable, OnDestroy } from '@angular/core';
import { BenutzermanagementAPIService } from '../api/benutzermanagement/benutzermanagement-api.service';
import { Store } from '@ngrx/store';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { getBenutzerInfoFeatureState } from '../reducers/authinfo.reducer';
import { Subscription } from 'rxjs';
import { AgbDatenschutzAktualisierungComponent } from './agb-datenschutz-aktualisierung/agb-datenschutz-aktualisierung.component';
import { getExtradataInstanceFeatureState } from '../reducers/userdata.reducer';
import { environment } from '../../environments/environment';
import { LoadingState } from '../shared/reducer.includes';
import { FooterModalsService } from '../shared/footer-modals.service';
import { LexOnlineAktivierungPremiumInfoPopupComponent } from './lex-online-aktivierung-premium-info-popup/lex-online-aktivierung-premium-info-popup.component';
import { TelefonnummerWirdPflichtfeldPopupComponent } from './telefonnummer-wird-pflichtfeld/telefonnummer-wird-pflichtfeld-popup.component';
import { HybridDRGAbrechnungFlyerPopupComponent } from './hybrid-drgabrechnung-flyer-popup/hybrid-drgabrechnung-flyer-popup.component';
import { NovitasBKKHKSInformationPopupComponent } from './novitas-bkkhksinformation-popup/novitas-bkkhksinformation-popup.component';
import { BkkTextilgruppeFusion } from './bkk-textilgruppe-fusion/bkk-textilgruppe-fusion.component';
import { CustomizablePopupComponent } from './customizable-popup/customizable-popup.component';
import { VersorgenStattSparenComponent } from './versorgen-statt-sparen/versorgen-statt-sparen.component';

@Injectable({
  providedIn: 'root',
})
export class UserPopupsService implements OnDestroy {
  private popups: { definedPopups: string[]; customPopups: string[] } = {
    definedPopups: [],
    customPopups: [],
  };
  private benutzerInfoSub$: Subscription;
  private benutzerEDISub$: Subscription;
  private arztEinschreibungenSub$: Subscription;
  private knownPopups: {
    [key: string]: {
      component: any;
      size: 'm' | 'xl' | 'lg';
      backdrop?: 'static' | boolean;
    };
  } = {
    'AGB & Datenschutz Aktualisierung (v2)': {
      component: AgbDatenschutzAktualisierungComponent,
      size: 'xl',
    },
    'Lex Online Aktivierung Premium Info': {
      component: LexOnlineAktivierungPremiumInfoPopupComponent,
      size: 'lg',
    },
    'Telefonnummer wird Pflichtfeld': {
      component: TelefonnummerWirdPflichtfeldPopupComponent,
      size: 'lg',
    },
    'Hybrid DRG Abrechnung Flyer': {
      component: HybridDRGAbrechnungFlyerPopupComponent,
      size: 'xl',
      backdrop: true,
    },
    'Novitas BKK HKS Information': {
      component: NovitasBKKHKSInformationPopupComponent,
      size: 'lg',
    },
    'BKK Textilgruppe Fusion': {
      component: BkkTextilgruppeFusion,
      size: 'lg',
    },
    'versorgen-statt-sparen': {
      component: VersorgenStattSparenComponent,
      size: 'xl',
    },
  };
  private currentModal: NgbModalRef;
  private shownPopups: Set<string> = new Set<string>();

  constructor(
    private benutzerApiClient: BenutzermanagementAPIService,
    private store: Store,
    private modalService: NgbModal,
    private fms: FooterModalsService,
  ) {
    this.benutzerInfoSub$ = this.store
      .select(getBenutzerInfoFeatureState)
      .subscribe((value) => {
        if (
          value.auth.isAuthenticated &&
          value.products.state === LoadingState.Loaded
        ) {
          this.benutzerApiClient.getPopups().subscribe((popups) => {
            for (const customPopup of popups.customPopups) {
              this.popups.customPopups.push(customPopup);
            }
            for (const popup of popups.definedPopups) {
              this.addPopup(popup);
            }
          });
        }
      });
    this.benutzerEDISub$ = this.store
      .select(getExtradataInstanceFeatureState)
      .subscribe((value) => {
        if (value?.ExtradataInstances.loadingState !== LoadingState.Loaded) {
          return;
        }
        let showAgbPopup = false;
        let showTelefonnummerPopup = true;

        for (const d of value.ExtradataInstances.alleExtradataInstances) {
          const key = `${d.extradata.namespace}/${d.extradata.name}`;
          if (
            key === 'Sanakey/AGB akzeptiert (Version)' &&
            (d.value as number) < environment.version_agb
          ) {
            showAgbPopup = true;
          }
          if (
            key === 'Sanakey/Datenschutzbestimmungen akzeptiert (Version)' &&
            (d.value as number) < environment.version_datenschutz
          ) {
            showAgbPopup = true;
          }
          if (key === 'Sanakey/Telefonnummer' && d.value) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            showTelefonnummerPopup = false;
          }
        }

        if (showAgbPopup) {
          this.addPopup('AGB & Datenschutz Aktualisierung (v2)');
        }
      });
  }

  ngOnDestroy(): void {
    this.benutzerInfoSub$?.unsubscribe();
    this.benutzerEDISub$?.unsubscribe();
    this.arztEinschreibungenSub$?.unsubscribe();
  }

  private addPopup(popup: string) {
    if (this.popups.definedPopups.indexOf(popup) >= 0) {
      return;
    }
    this.popups.definedPopups.push(popup);
    this.showNextPopup();
  }

  private getPopupComponent(popupName: string) {
    if (this.popups.customPopups.includes(popupName)) {
      return {
        component: CustomizablePopupComponent,
        size: 'lg',
        backdrop: 'static',
      };
    }
    return this.knownPopups[popupName];
  }

  private showNextPopup() {
    this.fms.closeProcessError();
    if (this.currentModal) {
      return;
    }

    if (this.popups.definedPopups.length) {
      const popupName = this.popups.definedPopups.shift();
      const popupComponent = this.getPopupComponent(popupName);
      if (!popupComponent) {
        console.warn('Popup unbekannt: ' + popupName);
        setTimeout(this.showNextPopup.bind(this));
        return;
      }
      if (this.shownPopups.has(popupName)) {
        setTimeout(this.showNextPopup.bind(this));
        return;
      }

      this.shownPopups.add(popupName);

      let backdrop: 'static' | boolean = 'static';
      if (
        popupComponent.backdrop === false ||
        popupComponent.backdrop === true
      ) {
        backdrop = popupComponent.backdrop;
      }

      this.currentModal = this.modalService.open(popupComponent.component, {
        backdrop,
        size: popupComponent.size,
        keyboard: false,
      });
      if (popupComponent.component === CustomizablePopupComponent) {
        (this.currentModal.componentInstance as CustomizablePopupComponent).ID =
          popupName;
      }
      this.currentModal.result.finally(this.resetAndShowNextPopup.bind(this));
    }
  }

  private resetAndShowNextPopup() {
    this.currentModal = null;
    this.showNextPopup();
  }
}
